import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics } from '@/mocks/data';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-lg text-foreground-500">漫画未找到</p>
          <Link to="/" className="text-accent-500 mt-2 hover:underline">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫大全`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2025-01-01',
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩漫大全',
    },
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: comic.categoryName, href: `/category/${comic.category}` },
          { label: comic.title },
        ]}
      />

      <section className="bg-background-50 py-10 md:py-16">
        <div className="px-4 md:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="w-full lg:w-[45%]">
              <div className="relative rounded-xl overflow-hidden bg-background-100">
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫大全`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <span className="absolute top-4 left-4 bg-background-50 text-foreground-900 text-xs font-medium px-3 py-1 rounded-full">
                  {comic.statusText}
                </span>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-background-50 text-sm font-medium">{comic.latestChapter}</p>
                  <p className="text-background-50/60 text-xs mt-1">更新于 {comic.updateDate}</p>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[55%] flex flex-col justify-center">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-accent-100 text-accent-700 px-2.5 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                {comic.description}
              </p>

              <Link
                to={`/category/${comic.category}`}
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors w-fit"
              >
                立即阅读 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>

              <div className="flex gap-10 mt-8">
                <div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-900">{comic.popularity}</p>
                  <p className="text-xs text-foreground-400 mt-1">总人气</p>
                </div>
                <div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground-900">{comic.rating}</p>
                  <p className="text-xs text-foreground-400 mt-1">评分</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-foreground-900 py-14 md:py-20">
        <div className="px-4 md:px-6 lg:px-10">
          <div className="flex items-start gap-8 mb-10">
            <span className="whitespace-nowrap cursor-pointer border border-background-50/30 text-background-50 px-4 py-1.5 rounded-full text-xs font-medium">
              章节列表
            </span>
            <h2 className="text-3xl md:text-5xl font-bold font-heading text-background-50 leading-tight">
              共 {comic.chapters} 话
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: Math.min(comic.chapters, 24) }, (_, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-lg px-4 py-3 text-center cursor-pointer hover:bg-accent-100 transition-colors"
              >
                <p className="text-sm font-medium text-foreground-900">第 {comic.chapters - i} 话</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {relatedComics.length > 0 && (
        <section className="bg-background-50 py-14 md:py-20">
          <div className="px-4 md:px-6 lg:px-10">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-3">
              相关推荐
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              更多{comic.categoryName}类型佳作
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {relatedComics.map((rc) => (
                <Link key={rc.id} to={`/comic/${rc.id}`} className="group">
                  <div className="bg-background-100 rounded-lg overflow-hidden">
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        title={`${rc.title} - 韩漫大全`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-foreground-900 group-hover:text-accent-500 transition-colors line-clamp-1">
                      {rc.title}
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">{rc.author}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}